import { useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Modal,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';

import { HeaderGoBack } from '@components/HeaderGoBack/HeaderGoBack';
import { ModalHeader } from '@components/Headers/ModalHeader';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/RestyleIcon';
import {
  Invoice,
  ListTeamInvoicesDocument,
  useCancelStripeSubscriptionMutation,
  useGetTeamQuery,
} from '@graphql/generated';
import { TeamsStackScreenProps } from '@navigation/teams/teams-stack';
import { teamStyles } from '@screens/Members/EditNewMemberRoles';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

const TeamSubscriptionListFooter = () => <Box marginBottom='listFooter' />;
const TeamSubscriptionItemSeparator = () => <Box marginBottom='l' />;

export const TeamSubscription = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const route = useRoute<TeamsStackScreenProps<'team-subscription'>['route']>();
  const { trackEvent } = useMixpanel();

  const {
    params: { teamUUId },
  } = route || { teamUUId: '' };

  const { data: teamData, loading } = useGetTeamQuery({
    variables: { id: teamUUId, includePending: true },
    skip: teamUUId?.length < 1,
  });

  const { getTeam: teamDetail } = teamData || {};

  const [teamId, setTeamId] = useState<string>('');

  useEffect(() => {
    teamDetail?.id && setTeamId(teamDetail?.id);
    if (teamDetail?.id) {
      trackEvent(MIXPANEL_EVENTS.OPENED_INVOICE_TAB, {
        team_id: teamDetail.id,
      });
    }
  }, [teamDetail, trackEvent]);

  const [cancelStripeSubscription, { loading: cancellationLoading }] =
    useCancelStripeSubscriptionMutation({
      variables: { id: teamId },
      refetchQueries: teamUUId
        ? [{ query: ListTeamInvoicesDocument, variables: { id: teamUUId } }]
        : [],
    });

  const { invoices, subscriptionDetails } = teamDetail || {};

  const invoicesListHeader = useCallback(
    () => (
      <Box>
        <Text variant='labelEmphasized' mb='m'>
          {t('models:teams.subscriptions.invoicesLabel', {
            count: invoices?.length,
          })}
        </Text>
      </Box>
    ),
    [t, invoices?.length]
  );

  const planDetails = () => (
    <Box padding='m' borderColor='grey02' borderRadius='s' borderWidth={1}>
      <Box flexDirection='row' justifyContent='space-between' mb='l'>
        <Text variant='labelEmphasized'>
          {t('models:teams.subscriptions.plan')}
        </Text>

        <Box flexDirection='row' alignItems='center'>
          <Box
            width={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
            height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
            backgroundColor='purple'
            alignItems='center'
            justifyContent='center'
            borderRadius='xs'>
            <Icon
              name='Star' // TODO get this from metadata?
              color='background'
            />
          </Box>
          <Text variant='body' ml='xs' mr='m'>
            {subscriptionDetails?.subscriptionPlanName}
          </Text>
        </Box>
      </Box>
      <Box flexDirection='row' justifyContent='space-between' mb='l'>
        <Text variant='labelEmphasized'>
          {t('models:teams.subscriptions.nextInvoice')}
        </Text>
        <Text variant='body'>
          {t('models:teams.subscriptions.nextInvoiceInfo', {
            amount: (
              (Math.max(Number(subscriptionDetails?.pricePerSeat ?? '0'), 1) /
                100) *
              (subscriptionDetails?.currentSeatCount ?? 1)
            ).toFixed(2),
            date: t('format:dateSlash', {
              val: subscriptionDetails?.expirationDate,
            }),
          })}
        </Text>
      </Box>
      <Box flexDirection='row' justifyContent='space-between'>
        <Text variant='labelEmphasized'>
          {t('models:teams.subscriptions.paymentMethod')}
        </Text>
        {subscriptionDetails?.paymentCardLast4 && (
          <Box flexDirection='row' justifyContent='center' alignItems='center'>
            <Icon name='Visa' variant='xxl' color='white' />
            <Text variant='labelSmall' color='textSecondary' ml='xs'>
              **** **** **** {subscriptionDetails?.paymentCardLast4}
            </Text>
          </Box>
        )}
        {!subscriptionDetails?.paymentCardLast4 && (
          <Box flexDirection='row' justifyContent='center' alignItems='center'>
            <Text variant='labelSmall' color='textSecondary'>
              Not found.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );

  const invoiceItem = ({ item }: { item: Invoice }) => (
    <Box
      padding='m'
      borderColor='grey02'
      borderRadius='s'
      borderWidth={1}
      flexDirection='row'
      justifyContent='space-between'>
      <Box flex={1}>
        <Text variant='body'>
          {t('models:teams.subscriptions.invoices.date')}
        </Text>
        <Text variant='labelEmphasized' mb='m'>
          {t('format:timeAgoTasksTableList', { val: item.date })}
        </Text>

        <Text variant='body'>
          {t('models:teams.subscriptions.invoices.description')}
        </Text>
        <Text variant='labelEmphasized'>{item.description}</Text>
      </Box>
      <Box alignItems='flex-end' ml='m'>
        <Text variant='body'>
          {t('models:teams.subscriptions.invoices.invoiceTotal')}
        </Text>
        <Text variant='labelEmphasized' mb='m'>
          {t('models:teams.subscriptions.invoices.invoiceTotalAmount', {
            amount: (Math.max(item.amount, 1) / 100).toFixed(2),
          })}
        </Text>

        <Text variant='body'>
          {t('models:teams.subscriptions.invoices.status', {
            context: item.status.toLowerCase(),
          })}
        </Text>
        {!!item.invoiceUrl && (
          <TouchableOpacity
            onPress={() =>
              Linking.openURL(item.invoiceUrl!).catch(console.error)
            }>
            <Text variant='labelEmphasized' color='greenSecondary'>
              {t('models:teams.subscriptions.invoices.viewInvoice')}
            </Text>
          </TouchableOpacity>
        )}
      </Box>
    </Box>
  );
  return (
    <Box
      flex={1}
      padding='m'
      style={Platform.OS === 'web' ? { marginEnd: 300 + theme.spacing.m } : {}}
      marginTop={Platform?.OS !== 'web' ? 'xxl' : 'xxxs'}>
      <Modal animationType='fade' transparent={true} visible={modalOpen}>
        <Box style={teamStyles.modalContainer}>
          <TouchableWithoutFeedback
            onPress={() => setModalOpen(!modalOpen)}
            style={teamStyles.modal}
            accessibilityLabel='CloseModalButton'>
            <Box style={teamStyles.modal} />
          </TouchableWithoutFeedback>

          <ShadowBox
            variant='thin'
            style={
              Platform.OS === 'web' ? teamStyles.popUpWeb : teamStyles.popUp
            }>
            <Box
              marginVertical='m'
              marginHorizontal='m'
              flex={1}
              justifyContent='space-between'>
              <Box
                style={teamStyles.modalHeader}
                mb='l'
                accessibilityLabel='RoleSelectModal'>
                <ModalHeader
                  title={t('models:teams.subscriptions.cancelModal.title')}
                  // submitLabel={t('shared:cancel')}
                  onCancel={() => setModalOpen(false)}
                  showCancel={false}
                  onSubmit={() => setModalOpen(false)}
                />
              </Box>
              <Text variant='labelEmphasized'>
                {t('models:teams.subscriptions.cancelModal.subTitle')}
              </Text>
              <Text>
                <Text mr='xxs' mb='s'>
                  {t('models:teams.subscriptions.cancelModal.accessThru')}
                </Text>
                <Text variant='labelEmphasized'>
                  {t('format:dateSlash', {
                    val: subscriptionDetails?.expirationDate,
                  })}
                </Text>
              </Text>
              <Box flexDirection='row' mt='m' justifyContent='space-between'>
                <Button
                  variant='primary'
                  onPress={() => {
                    setModalOpen(false);
                  }}
                  fullWidth={false}>
                  {t('models:teams.subscriptions.cancelModal.cancel')}
                </Button>
                <Button
                  variant='delete'
                  disabled={cancellationLoading}
                  onPress={() => {
                    setModalOpen(false);
                    cancelStripeSubscription();
                    if (Platform.OS === 'web') {
                      window.location.reload();
                    }
                  }}
                  fullWidth={false}>
                  {t('models:teams.subscriptions.cancelModal.confirm')}
                </Button>
              </Box>
            </Box>
          </ShadowBox>
        </Box>
      </Modal>
      <HeaderGoBack
        redirectTo='team-details'
        titleHeader='Subscription'
        params={{ teamUUId }}
        noSpacing
      />
      <Box mb='m'>{planDetails()}</Box>

      {loading && (
        <Box>
          <ActivityIndicator />
        </Box>
      )}
      {!loading && (
        <FlatList
          showsVerticalScrollIndicator={false}
          data={invoices || []}
          keyExtractor={(item) => item.id}
          ListEmptyComponent={<Text>No invoices yet</Text>}
          renderItem={invoiceItem}
          ListFooterComponent={TeamSubscriptionListFooter}
          ListHeaderComponent={invoicesListHeader}
          ItemSeparatorComponent={TeamSubscriptionItemSeparator}
        />
      )}
    </Box>
  );
};
